import React, { useState, useEffect, useRef, useCallback } from "react";
import { Button, Col, Container, Row, Input, Label, Dropdown, DropdownMenu, DropdownItem, Spinner } from "reactstrap";
import { useDispatch } from "react-redux";
import "react-toastify/dist/ReactToastify.css";
import {
  getCommissions,
  getCommissionsAdmin,
  archiveCommission,
  searchUsersAdmin,
} from "../../slices/general/thunk";
import CommissionsTable from "../../Components/Tables/CommissionsTable/CommissionsTable";
import { useCurrentUser } from "../../slices/auth/hooks";
import Loading from "../../Components/Loading";
import { DASHBOARD_USER_ROLES } from "../../constants";
import NavTabCommissions from "../../Components/Tabs/NavTabCommissions";
import { useParams } from "react-router-dom";
import HeadersRole from "../../Components/Headers/HeadersRole";
import commissionsIcon from "../../assets/images/iconsRoles/commissionsIcon.svg";
import UserInfoCard from "../../Components/UserInfoCard/UserInfoCard";
import { parsedInvoiceAmount } from "../../utils/utils";
import AddCommissionModal from "../../Components/modals/AddCommission/AddCommissionModal";
import Swal from "sweetalert2";

// Generate month options for the last 12 months
const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
    const label = date.toLocaleDateString("en-US", { year: "numeric", month: "long" });
    options.push({ value, label });
  }
  return options;
};

const DashboardCommissions = () => {
  const { id } = useParams();
  const currentUser = useCurrentUser();
  const dispatch = useDispatch();

  const isAdmin = currentUser.role === DASHBOARD_USER_ROLES.ADMIN;
  const isProfilePage = id;

  const [firstCommission, setFirstCommission] = useState(null);

  const [isModalAddCommissionOpen, setIsModalAddCommissionOpen] =
    useState(false);

  const toggleModalCommission = () => {
    setIsModalAddCommissionOpen(!isModalAddCommissionOpen);
  };

  const [loading, setLoading] = useState(false);
  const [commissions, setCommissions] = useState([]);
  const [pageSize, setPageSize] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const [currentPage, setCurrentPage] = useState(0);

  const [activeTab, setActiveTab] = useState("1");

  // Filter states
  const [filterMonth, setFilterMonth] = useState("");
  const [filterRole, setFilterRole] = useState("");
  const [isArchived, setIsArchived] = useState(false);
  const [isPaid, setIsPaid] = useState(false);

  // User search states
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const searchTimeoutRef = useRef(null);
  const searchInputRef = useRef(null);

  const toggleTab = (tab, status) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setCurrentPage(0);

      // Set filter states based on tab
      if (status === "due") {
        setIsPaid(false);
        setIsArchived(false);
      } else if (status === "paid") {
        setIsPaid(true);
        setIsArchived(false);
      } else if (status === "archived") {
        setIsArchived(true);
      }
    }
  };

  const handleChangePage = (page) => {
    setCurrentPage(page);
  };

  const handleFilterChange = () => {
    setCurrentPage(0);
  };

  // Debounced user search
  const searchUsers = useCallback(async (query) => {
    if (!query || query.length < 2) {
      setUserSearchResults([]);
      setUserDropdownOpen(false);
      return;
    }

    setUserSearchLoading(true);
    try {
      const response = await dispatch(
        searchUsersAdmin({
          query,
          role: filterRole || undefined,
          pageSize: 10,
        })
      ).unwrap();

      if (response.data) {
        setUserSearchResults(response.data);
        setUserDropdownOpen(response.data.length > 0);
      }
    } catch (error) {
      console.log(error);
      setUserSearchResults([]);
    } finally {
      setUserSearchLoading(false);
    }
  }, [dispatch, filterRole]);

  const handleUserSearchChange = (e) => {
    const query = e.target.value;
    setUserSearchQuery(query);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search
    searchTimeoutRef.current = setTimeout(() => {
      searchUsers(query);
    }, 300);
  };

  const handleSelectUser = (user) => {
    setSelectedUser(user);
    setUserSearchQuery("");
    setUserSearchResults([]);
    setUserDropdownOpen(false);
    setCurrentPage(0);
  };

  const handleClearSelectedUser = () => {
    setSelectedUser(null);
    setCurrentPage(0);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchInputRef.current && !searchInputRef.current.contains(event.target)) {
        setUserDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleArchiveCommission = async (commission) => {
    const isCurrentlyArchived = commission.archived;
    const action = isCurrentlyArchived ? "unarchive" : "archive";
    const userInfo = commission.dashboardUserInfo;
    const userName = userInfo?.companyName || `${userInfo?.firstName || ''} ${userInfo?.lastName || ''}`.trim() || 'this user';

    const result = await Swal.fire({
      title: `${isCurrentlyArchived ? "Unarchive" : "Archive"} Commission`,
      text: `Are you sure you want to ${action} this commission for ${userName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: isCurrentlyArchived ? "#3085d6" : "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: `Yes, ${action} it`,
    });

    if (result.isConfirmed) {
      try {
        const response = await dispatch(
          archiveCommission({
            commissionId: commission.id,
            archived: !isCurrentlyArchived,
          })
        ).unwrap();

        if (response.success) {
          Swal.fire({
            title: "Success",
            text: `Commission ${action}d successfully`,
            icon: "success",
            timer: 1500,
          });
          // Refresh the list
          if (isAdmin && !id) {
            fetchCommissionsAdmin();
          } else {
            fetchCommissions();
          }
        } else {
          Swal.fire({
            title: "Error",
            text: response.error || `Failed to ${action} commission`,
            icon: "error",
          });
        }
      } catch (error) {
        Swal.fire({
          title: "Error",
          text: `Failed to ${action} commission`,
          icon: "error",
        });
      }
    }
  };

  const fetchCommissions = async () => {
    const userIdToFetch = id ? id : currentUser.id;
    try {
      setLoading(true);
      const response = await dispatch(
        getCommissions({
          userId: userIdToFetch,
          page: currentPage,
        })
      ).unwrap();

      const res = response.data;
      setCommissions(res);
      if (currentPage === 0 && res.length > 0) {
        setFirstCommission(res[0].totalAmount);
      }
      setTotalPages(response.pages);
      setPageSize(response.pageSize);
      setTotalItems(response.total);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const fetchCommissionsAdmin = async () => {
    try {
      setLoading(true);
      const response = await dispatch(
        getCommissionsAdmin({
          paid: isPaid,
          page: currentPage,
          month: filterMonth || undefined,
          role: filterRole || undefined,
          archived: isArchived,
          dashboardUserId: selectedUser?.id || undefined,
        })
      ).unwrap();

      if (!response.error) {
        setCommissions(response.data);
        setTotalPages(response.pages);
        setPageSize(response.pageSize);
        setTotalItems(response.total);
        setLoading(false);
      } else {
        setLoading(false);
      }
    } catch (error) {
      console.log(error);
      setCommissions([]);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && !id) {
      fetchCommissionsAdmin();
    } else {
      fetchCommissions();
    }
  }, [isPaid, isArchived, currentPage, activeTab, filterMonth, filterRole, selectedUser]);

  const shouldRenderMonthlyCommissions =
    firstCommission &&
    ((!isProfilePage && DASHBOARD_USER_ROLES.NATIONAL === currentUser.role) ||
      DASHBOARD_USER_ROLES.BROKER === currentUser.role ||
      DASHBOARD_USER_ROLES.GA === currentUser.role);

  return (
    <div className={id ? "mt-3" : "page-content px-0"}>
      <AddCommissionModal
        open={isModalAddCommissionOpen}
        setOpen={setIsModalAddCommissionOpen}
        onRefresh={fetchCommissions}
      />
      <Container fluid>
        <HeadersRole
          role={isAdmin && !isProfilePage ? "Pay Commissions" : "Commissions"}
          icon={commissionsIcon}
          svg={true}
        />
        {shouldRenderMonthlyCommissions && (
          <Col xl={4} className="mb-4 mt-n4">
            <UserInfoCard
              role="My Monthly Commissions"
              invitedByUserId={currentUser.id}
              montlyCommission={true}
              info={currentUser}
              otherProps={{
                loadingCommissions: loading,
                firstCommission: parsedInvoiceAmount(firstCommission),
              }}
              icon={commissionsIcon}
            />
          </Col>
        )}
        <div className={`mb-4 pb-3 ${isProfilePage ? "" : "card"} `}>
          <div className={`pt-0 ${isProfilePage ? "" : "card-body mt-3"}`}>
            {isProfilePage && isAdmin && (
              <div>
                <Button
                  onClick={toggleModalCommission}
                  className="btn btn-soft-primary border border-primary"
                >
                  Add Commission Manually
                </Button>
              </div>
            )}
            <div
              style={{ paddingTop: id ? "7px" : "0px" }}
              className="col-4 d-flex mt-2 justify-content-end "
            ></div>
            <Col xl={12} className={`${!id ? "mt-4" : ""}`}>
              {isAdmin && !isProfilePage && (
                <>
                  <NavTabCommissions
                    loading={loading}
                    toggleTab={toggleTab}
                    activeTab={activeTab}
                  />

                  {/* Filters Row */}
                  <Row className="mb-3 g-3 align-items-end">
                    <Col md={2}>
                      <Label className="form-label mb-1 text-muted small">
                        Filter by Month
                      </Label>
                      <Input
                        type="select"
                        value={filterMonth}
                        onChange={(e) => {
                          setFilterMonth(e.target.value);
                          setCurrentPage(0);
                        }}
                        disabled={loading}
                      >
                        <option value="">All Months</option>
                        {getMonthOptions().map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </Input>
                    </Col>
                    <Col md={2}>
                      <Label className="form-label mb-1 text-muted small">
                        Filter by Entity
                      </Label>
                      <Input
                        type="select"
                        value={filterRole}
                        onChange={(e) => {
                          setFilterRole(e.target.value);
                          setSelectedUser(null);
                          setCurrentPage(0);
                        }}
                        disabled={loading}
                      >
                        <option value="">All Entities</option>
                        <option value="broker">Broker</option>
                        <option value="ga">GA</option>
                        <option value="national">National</option>
                      </Input>
                    </Col>
                    <Col md={4}>
                      <Label className="form-label mb-1 text-muted small">
                        Search User
                      </Label>
                      {selectedUser ? (
                        <div className="d-flex align-items-center border rounded px-2 py-1 bg-light">
                          <span className="flex-grow-1 text-truncate">
                            {selectedUser.companyName || `${selectedUser.firstName} ${selectedUser.lastName}`}
                            <small className="text-muted ms-1">({selectedUser.email})</small>
                          </span>
                          <Button
                            color="link"
                            size="sm"
                            className="p-0 ms-2 text-danger"
                            onClick={handleClearSelectedUser}
                          >
                            <i className="ri-close-line"></i>
                          </Button>
                        </div>
                      ) : (
                        <div className="position-relative" ref={searchInputRef}>
                          <Input
                            type="text"
                            placeholder="Type to search by name or email..."
                            value={userSearchQuery}
                            onChange={handleUserSearchChange}
                            onFocus={() => {
                              if (userSearchResults.length > 0) {
                                setUserDropdownOpen(true);
                              }
                            }}
                            disabled={loading}
                          />
                          {userSearchLoading && (
                            <Spinner
                              size="sm"
                              className="position-absolute"
                              style={{ right: "10px", top: "50%", transform: "translateY(-50%)" }}
                            />
                          )}
                          {userDropdownOpen && userSearchResults.length > 0 && (
                            <div
                              className="position-absolute w-100 bg-white border rounded shadow-sm mt-1"
                              style={{ zIndex: 1050, maxHeight: "250px", overflowY: "auto" }}
                            >
                              {userSearchResults.map((user) => (
                                <div
                                  key={user.id}
                                  className="px-3 py-2 cursor-pointer hover-bg-light border-bottom"
                                  style={{ cursor: "pointer" }}
                                  onClick={() => handleSelectUser(user)}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = "#f8f9fa"}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = "transparent"}
                                >
                                  <div className="fw-medium">
                                    {user.companyName || `${user.firstName} ${user.lastName}`}
                                  </div>
                                  <small className="text-muted">{user.email}</small>
                                  {user.role && (
                                    <span className="badge bg-soft-primary text-primary ms-2">
                                      {user.role}
                                    </span>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </Col>
                    <Col md={2}>
                      {(filterMonth || filterRole || selectedUser) && (
                        <Button
                          color="link"
                          className="text-muted p-0"
                          onClick={() => {
                            setFilterMonth("");
                            setFilterRole("");
                            setSelectedUser(null);
                            setCurrentPage(0);
                          }}
                        >
                          <i className="ri-close-line me-1"></i>
                          Clear Filters
                        </Button>
                      )}
                    </Col>
                  </Row>
                </>
              )}

              <div className={`table-container   position-relative `}>
                {loading && <Loading />}
                <div className="live-preview">
                  <div className="table-responsive ">
                    <CommissionsTable
                      isAdmin={isAdmin}
                      loading={loading}
                      data={commissions}
                      paginationProps={{
                        currentPage,
                        totalPages,
                        totalItems,
                        pageSize,
                        onChangePage: handleChangePage,
                      }}
                      onArchive={isAdmin && !isProfilePage ? handleArchiveCommission : null}
                      showArchived={isArchived}
                    />
                  </div>
                </div>
              </div>
            </Col>
          </div>
        </div>
      </Container>
    </div>
  );
};

export default DashboardCommissions;
